import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import { Button } from "@/v5/components/ui/button";
import {
  listPatientDocuments,
  type PatientDocument,
} from "@/services/patient-documents.service";
import { Check, FileText, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface DocumentSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId?: string;
  /** Currently selected document file names (strActivity). */
  selected: string[];
  onConfirm: (fileNames: string[]) => void;
}

export function DocumentSelectModal({
  open,
  onOpenChange,
  patientId,
  selected,
  onConfirm,
}: DocumentSelectModalProps) {
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setPicked(new Set(selected));
    if (!patientId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    listPatientDocuments(Number(patientId))
      .then((docs) => setDocuments(docs.filter((d) => d.fileName)))
      .catch(() => setDocuments([]))
      .finally(() => setLoading(false));
  }, [open, patientId, selected]);

  const toggle = useCallback((fileName: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(fileName)) {
        next.delete(fileName);
      } else {
        next.add(fileName);
      }
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(Array.from(picked));
    onOpenChange(false);
  }, [picked, onConfirm, onOpenChange]);

  const formatDate = (date: string | null, time: string): string => {
    const raw = [date, time].filter(Boolean).join(" ").trim();
    if (!raw) return "";
    const parsed = new Date(raw.replace(" ", "T"));
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Select Document(s)
          </DialogTitle>
          <DialogDescription>
            Attach previously generated documents for this patient.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            {documents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No documents to display.
              </p>
            ) : (
              <div className="space-y-1.5 pb-2">
                {documents.map((doc) => {
                  const isPicked = picked.has(doc.fileName);
                  return (
                    <button
                      key={`${doc.commLogId}-${doc.fileName}`}
                      type="button"
                      onClick={() => toggle(doc.fileName)}
                      className="w-full text-left rounded-lg border border-border px-3 py-2.5 text-sm transition-colors hover:bg-muted/60 hover:border-primary/40 flex items-start gap-3"
                    >
                      <span
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          isPicked
                            ? "bg-primary border-primary text-white"
                            : "border-muted-foreground/40"
                        }`}
                      >
                        {isPicked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium truncate">
                          {doc.subject?.trim() || doc.fileName}
                        </span>
                        <span className="block text-xs text-muted-foreground truncate">
                          {doc.fileName}
                        </span>
                        {formatDate(doc.date, doc.time) && (
                          <span className="block text-xs text-muted-foreground">
                            {formatDate(doc.date, doc.time)}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Attach{picked.size > 0 ? ` (${picked.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
